import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { formatRupiah } from "@/lib/assessmentConfig";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Loader2, Sparkles, RefreshCw, MapPin, Wallet, Clock, Target, Wrench, Package,
  ShieldAlert, Lightbulb, Users, TrendingUp, Repeat, Gauge, CircleDollarSign,
  FlaskConical, ArrowLeft, Info, ClipboardList, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const CASHFLOW_STYLE = { "Cepat": "bg-brand/15 text-brand", "Sedang": "bg-brand-amber/15 text-brand-amber", "Lebih Lama": "bg-slate-200 text-slate-600" };
const LEVEL_STYLE = { "Tinggi": "bg-brand/15 text-brand", "Sedang": "bg-brand-amber/15 text-brand-amber", "Rendah": "bg-slate-200 text-slate-600" };
const OPS_STYLE = { "Ringan": "bg-brand/15 text-brand", "Sedang": "bg-brand-amber/15 text-brand-amber", "Tinggi": "bg-slate-200 text-slate-600" };
const FIT_STYLE = { "Sangat sesuai": "bg-brand text-white", "Sesuai": "bg-brand-accent text-white", "Cukup sesuai": "bg-brand-amber text-white" };

export default function Ideas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [profileDone, setProfileDone] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [proceeding, setProceeding] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ideasRes, assessRes, valRes] = await Promise.all([
          api.get("/ideas").catch(() => ({ data: null })),
          api.get("/assessment").catch(() => ({ data: null })),
          api.get("/validation").catch(() => ({ data: null })),
        ]);
        setData(ideasRes.data || null);
        setProfileDone(assessRes.data?.assessment_status === "completed");
        // Pre-select previously chosen ideas (match by name) so returning users see their picks.
        if (ideasRes.data?.ideas && valRes.data?.ideas?.length === 2) {
          const names = valRes.data.ideas.map((v) => v.name);
          const idx = ideasRes.data.ideas
            .map((it, i) => (names.includes(it.name) ? i : -1))
            .filter((i) => i >= 0);
          if (idx.length === 2) setSelected(idx);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleSelect = (index) => {
    setSelected((prev) => {
      if (prev.includes(index)) return prev.filter((i) => i !== index);
      if (prev.length >= 2) {
        toast.error("Anda hanya dapat memilih 2 ide untuk dibandingkan lebih lanjut.");
        return prev;
      }
      return [...prev, index];
    });
  };

  const proceedValidation = async () => {
    if (selected.length !== 2) return;
    setProceeding(true);
    setError("");
    try {
      await api.post("/validation/select", { indices: selected });
      navigate("/validation");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menyimpan pilihan.");
      toast.error("Gagal menyimpan pilihan.");
    } finally {
      setProceeding(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data: res } = await api.post("/ideas/generate");
      setData(res);
      toast.success("Ide bisnis berhasil dibuat!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membuat ide. Coba lagi.");
      toast.error("Gagal membuat ide bisnis.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div>
      </DashboardLayout>
    );
  }

  // Profile not completed
  if (!profileDone && !data) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="ideas-need-profile">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><ClipboardList className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Lengkapi Profil Bisnis Dulu</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">IDEbisnis AI membutuhkan Profil Bisnis Anda untuk menyaring peluang yang sesuai.</p>
          <Button onClick={() => navigate("/assessment")} data-testid="go-assessment" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6">Mulai Analisis Bisnis</Button>
        </div>
      </DashboardLayout>
    );
  }

  // Generating state
  if (generating) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl text-center py-20 animate-fade-up" data-testid="ideas-generating">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
          <h2 className="mt-6 font-heading text-xl font-bold text-ink">Menyaring peluang untuk Anda…</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">
            IDEbisnis AI sedang menganalisis modal, waktu, skill, aset, dan batasan Anda untuk menemukan peluang yang paling masuk akal. Ini bisa memakan waktu hingga satu menit.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Empty (profile done, no ideas yet)
  if (!data) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="ideas-generate-cta">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Sparkles className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Profil Anda sudah siap.</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            IDEbisnis AI akan menggunakan profil ini untuk menyaring peluang bisnis yang lebih sesuai dengan kondisi Anda — maksimal 5 ide.
          </p>
          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="ideas-error">{error}</div>}
          <Button onClick={generate} data-testid="generate-ideas-btn" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6 text-base">
            <Sparkles className="mr-1.5 h-5 w-5" /> Buat 5 Ide Bisnis
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Results
  const b = data.basis || {};
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl animate-fade-up">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="ideas-back">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </button>

        {/* Basis */}
        <section className="mt-4 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="ideas-basis">
          <h2 className="font-heading text-xs font-bold uppercase tracking-wide text-brand-accent">Dasar Rekomendasi Anda</h2>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Basis icon={MapPin} label="Lokasi" value={b.location} />
            <Basis icon={Wallet} label="Modal dapat digunakan" value={formatRupiah(b.deployable_capital) || "-"} />
            <Basis icon={Clock} label="Waktu tersedia" value={b.available_time} />
            <Basis icon={Wrench} label="Skill utama" value={(b.key_skills || []).slice(0, 3).join(", ") || "-"} />
            <Basis icon={Package} label="Aset" value={(b.usable_assets || []).slice(0, 3).join(", ") || "-"} />
            <Basis icon={Target} label="Tujuan" value={b.goal} />
            <Basis icon={TrendingUp} label="Prioritas" value={(b.priorities || []).join(", ") || "-"} />
            <Basis icon={ShieldAlert} label="Toleransi risiko" value={b.risk_tolerance} />
            {b.key_constraints ? <Basis icon={Info} label="Batasan penting" value={b.key_constraints} /> : null}
          </div>
        </section>

        {/* Header + regenerate */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-ink">Peluang Bisnis yang Layak Anda Pertimbangkan</h1>
            <p className="mt-1 text-sm text-ink-soft">Pilih 2 ide untuk divalidasi lebih lanjut.</p>
          </div>
          <Button variant="outline" onClick={generate} data-testid="regenerate-btn" className="border-borderc gap-1.5">
            <RefreshCw className="h-4 w-4" /> Buat Ulang
          </Button>
        </div>

        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-sm font-semibold text-brand" data-testid="selection-counter">
          {selected.length} dari 2 ide dipilih
        </div>

        {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="ideas-error">{error}</div>}

        {data.note ? (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-brand-amber/30 bg-brand-amber/5 p-4 text-sm text-ink-soft" data-testid="ideas-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-amber" /> <span>{data.note}</span>
          </div>
        ) : null}

        {/* Idea cards */}
        <div className="mt-5 space-y-5" data-testid="idea-cards">
          {data.ideas.map((idea, i) => (
            <IdeaCard key={i} idea={idea} index={i} selected={selected.includes(i)} onToggle={() => toggleSelect(i)} />
          ))}
        </div>

        <p className="mt-8 text-xs text-ink-muted leading-relaxed">
          Rekomendasi ini adalah hipotesis awal berdasarkan kondisi Anda, bukan jaminan keuntungan. Angka bersifat estimasi/kisaran dan perlu divalidasi sebelum Anda mengeluarkan modal lebih besar.
        </p>
      </div>

      {/* Sticky validate CTA */}
      <div className="sticky bottom-0 z-30 mt-6 border-t border-borderc bg-white/95 backdrop-blur">
        <div className="mx-auto max-w-3xl px-4 sm:px-0 py-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-ink-soft">{selected.length} dari 2 ide dipilih</span>
          <Button onClick={proceedValidation} disabled={selected.length !== 2 || proceeding} data-testid="validate-cta" className="h-11 bg-brand hover:bg-brand-hover px-5">
            {proceeding ? <Loader2 className="h-5 w-5 animate-spin" /> : "Validasi 2 Ide Pilihan"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Basis({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-borderc bg-surface p-3">
      <div className="flex items-center gap-1.5 text-ink-muted"><Icon className="h-3.5 w-3.5" /><span className="text-[11px]">{label}</span></div>
      <div className="mt-1 text-sm font-semibold text-ink line-clamp-2">{value || "-"}</div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, styleMap, note }) {
  return (
    <div className="rounded-xl border border-borderc bg-surface p-3">
      <div className="flex items-center gap-1.5 text-ink-muted"><Icon className="h-3.5 w-3.5" /><span className="text-[11px]">{label}</span></div>
      <div className="mt-1.5">
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${styleMap[value] || "bg-slate-200 text-slate-600"}`}>{value}</span>
      </div>
      {note ? <p className="mt-1.5 text-[11px] text-ink-muted leading-snug">{note}</p> : null}
    </div>
  );
}

function IdeaCard({ idea, index, selected, onToggle }) {
  return (
    <article className={`rounded-2xl border bg-white p-5 sm:p-6 transition-colors ${selected ? "border-brand ring-1 ring-brand" : "border-borderc"}`} data-testid={`idea-card-${index}`} data-selected={selected}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="font-mono text-xs font-semibold text-brand-accent">IDE #{index + 1}</span>
          <h3 className="mt-1 font-heading text-lg sm:text-xl font-bold text-ink" data-testid={`idea-name-${index}`}>{idea.name}</h3>
          {idea.category ? <span className="mt-1 inline-block text-xs text-ink-muted">{idea.category}</span> : null}
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${FIT_STYLE[idea.fit_label] || "bg-slate-200 text-slate-600"}`} data-testid={`idea-fit-${index}`}>
          {idea.fit_label}
        </span>
      </div>

      <Block icon={Sparkles} title="Kenapa Cocok untuk Anda">{idea.why_fit}</Block>
      <Block icon={Users} title="Target Customer">{idea.target_customer}</Block>

      <div className="mt-4 rounded-xl border border-brand-accent/30 bg-brand/5 p-4">
        <div className="flex items-center gap-1.5 text-ink-muted"><CircleDollarSign className="h-4 w-4 text-brand" /><span className="text-xs font-semibold text-brand">Estimasi Modal Awal</span></div>
        <div className="mt-1 font-heading text-lg font-extrabold text-brand">{formatRupiah(idea.capital_min)} – {formatRupiah(idea.capital_max)}</div>
        {idea.capital_note ? <p className="mt-1 text-xs text-ink-soft leading-relaxed">{idea.capital_note}</p> : null}
      </div>

      <Block icon={TrendingUp} title="Model Pendapatan">{idea.revenue_model}</Block>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Metric icon={Gauge} label="Cashflow" value={idea.cashflow_speed} styleMap={CASHFLOW_STYLE} />
        <Metric icon={Repeat} label="Repeat Order" value={idea.repeat_order} styleMap={LEVEL_STYLE} />
        <Metric icon={Wrench} label="Operasional" value={idea.operational_level} styleMap={OPS_STYLE} />
      </div>
      {idea.cashflow_note ? <p className="mt-2 text-xs text-ink-muted">Cashflow: {idea.cashflow_note}</p> : null}

      {idea.leveraged_assets?.length ? (
        <Block icon={Package} title="Aset Anda yang Bisa Dimanfaatkan">{idea.leveraged_assets.join(", ")}</Block>
      ) : null}

      {idea.main_risks?.length ? (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-ink"><ShieldAlert className="h-4 w-4 text-brand-amber" /><span className="text-sm font-semibold">Risiko Utama</span></div>
          <ul className="mt-1.5 list-disc pl-6 text-sm text-ink-soft space-y-1">
            {idea.main_risks.map((r, k) => <li key={k}>{r}</li>)}
          </ul>
        </div>
      ) : null}

      <div className="mt-4 rounded-xl border border-borderc bg-surface p-4">
        <div className="flex items-center gap-1.5 text-ink"><FlaskConical className="h-4 w-4 text-brand-accent" /><span className="text-sm font-semibold">Yang Perlu Divalidasi</span></div>
        <p className="mt-1 text-sm text-ink-soft leading-relaxed">{idea.to_validate}</p>
      </div>

      <button
        type="button"
        onClick={onToggle}
        data-testid={`select-idea-${index}`}
        className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
          selected ? "border-brand bg-brand text-white" : "border-brand-accent bg-white text-brand hover:bg-brand/5"
        }`}
      >
        {selected ? <><CheckCircle2 className="h-4 w-4" /> Terpilih untuk Divalidasi</> : "Pilih untuk Divalidasi"}
      </button>
    </article>
  );
}

function Block({ icon: Icon, title, children }) {
  if (!children) return null;
  return (
    <div className="mt-4">
      <div className="flex items-center gap-1.5 text-ink"><Icon className="h-4 w-4 text-brand" /><span className="text-sm font-semibold">{title}</span></div>
      <p className="mt-1 text-sm text-ink-soft leading-relaxed">{children}</p>
    </div>
  );
}
